import { useState, useEffect } from 'react';
import {
  Stack,
  FormControl,
  FormLabel,
  HStack
} from '@chakra-ui/react';
import SearchBar from '../../SearchBar/SearchBar';
import StatusDropdown from '../../Dropdown/StatusDropdown/StatusDropdown';
import ArtistDropdown from '../../Artist/ArtistDropdown/ArtistDropdown';
import ScrapesiteDropdown from '../../Scrapesite/ScrapesiteDropdown/ScrapesiteDropdown';
import { ProcessStatus } from 'models/types';
import { ArtistModel } from 'models';
import { ScrapesiteModel } from 'models/scrapejob/ScrapesiteModel';

export interface ScrapeJobFiltersFormProps {
  defaultStatus?: ProcessStatus;
  defaultQuery?: string;
  defaultArtists?: ArtistModel[];
  defaultScrapesites?: ScrapesiteModel[];
  availableArtists?: ArtistModel[];
  availableScrapesites?: ScrapesiteModel[];
  onStatusChange: (status: ProcessStatus) => void;
  onQueryChange: (query: string) => void;
  onArtistChange: (artists: ArtistModel[]) => void;
  onScrapesiteChange: (scrapesites: ScrapesiteModel[]) => void;
}

export default function ScrapeJobFiltersForm({
  defaultStatus = ProcessStatus.PENDING,
  defaultQuery = '',
  defaultArtists = [],
  defaultScrapesites = [],
  availableArtists = [],
  availableScrapesites = [],
  onStatusChange,
  onQueryChange,
  onArtistChange,
  onScrapesiteChange
}: ScrapeJobFiltersFormProps) {
  const [selectedStatus, setSelectedStatus] = useState<ProcessStatus>(defaultStatus);
  const [query, setQuery] = useState<string>(defaultQuery);
  const [selectedArtists, setSelectedArtists] = useState<ArtistModel[]>(defaultArtists);
  const [selectedScrapesites, setSelectedScrapesites] = useState<ScrapesiteModel[]>(defaultScrapesites);

  // Sync internal state with prop changes
  useEffect(() => {
    setSelectedStatus(defaultStatus);
  }, [defaultStatus]);

  useEffect(() => {
    setQuery(defaultQuery);
  }, [defaultQuery]);

  useEffect(() => {
    setSelectedArtists(defaultArtists);
  }, [defaultArtists]);

  useEffect(() => {
    setSelectedScrapesites(defaultScrapesites);
  }, [defaultScrapesites]);

  const handleStatusChange = (status: ProcessStatus) => {
    setSelectedStatus(status);
    onStatusChange(status);
  };

  const handleQueryChange = (value: string) => {
    setQuery(value);
    onQueryChange(value);
  };

  const handleArtistChange = (artists: ArtistModel[]) => {
    setSelectedArtists(artists);
    onArtistChange(artists);
  };

  const handleScrapesiteChange = (scrapesites: ScrapesiteModel[]) => {
    setSelectedScrapesites(scrapesites);
    onScrapesiteChange(scrapesites);
  };

  return (
    <Stack spacing={4}>
      <HStack spacing={4} alignItems="flex-end">
        <FormControl flex={2}>
          <SearchBar
            onChange={handleQueryChange}
            defaultValue={defaultQuery}
          />
        </FormControl>
        <FormControl flex={1}>
          <FormLabel fontSize="sm" htmlFor="scrapejob-site">Site</FormLabel>
          <ScrapesiteDropdown
            scrapesites={availableScrapesites}
            defaultSelectedScrapesites={selectedScrapesites}
            onScrapesiteSelect={handleScrapesiteChange}
            multiSelectable={true}
            maxWidth={true}
          />
        </FormControl>
        <FormControl flex={1}>
          <FormLabel fontSize="sm" htmlFor="scrapejob-artist">Artist</FormLabel>
          <ArtistDropdown
            artists={availableArtists}
            onArtistSelect={handleArtistChange}
            defaultSelectedArtists={selectedArtists}
            multiSelectable={true}
            maxWidth={true}
          />
        </FormControl>
        <FormControl flex={1}>
          <FormLabel fontSize="sm" htmlFor="scrapejob-status">Status</FormLabel>
          <StatusDropdown
            value={selectedStatus}
            onChange={handleStatusChange}
            size="md"
          />
        </FormControl>
      </HStack>
    </Stack>
  );
}
